<template>
  		<o-loading :full-page="true" :active="isLoading" :can-cancel="false"></o-loading>
  <div class="list-view company-location">
    <TableGutter
      :tools="tableColOpts.gutterOpts"
      :selectedAction="false">
    </TableGutter>
    <div class="column" v-if="isDeleteMode">
      <div class="columns">
        <div class="column is-flex is-align-items-center has-background-grey-lighter">
          <button
            class="button has-text-weight-bold has-background-black-bis has-text-white"
            :disabled="!selectedRows.length"
            @click="bulkDeleteLocations"
          >
            Delete ({{ selectedRows.length }})
          </button>
          <button
            class="button is-outlined has-text-weight-bold ml-2"
            @click="cancelDeleteMode"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
    <table-toolbar v-else :key="refreshToolBar"
      searchPlaceHolder="Search Locations"
      :toolbarOpts="tableColOpts.toolBar"
      @handleAction="handleAction"/>
    <data-table :key="tableKey" :apiMode="true" :tableOpts="tableColOpts" :loadData="loadData"
      @checkbox-toggled="onCheckboxToggled" @open-modal="openNestedModal" @cell-clicked="onCellClicked"/>
  </div>
  <locations-form v-if="showCreateLocationModal" :isActive="showCreateLocationModal"
    @close="onLocationFormClose" :editMode="isEdit" :rowToEdit="rowToEdit"/>
    <o-modal :active="isExcelModalActive" :can-cancel="false">
      <div class="modal-card">
        <header class="card-header has-background-grey-lighter">
          <p class="card-header-title has-text-black-bis">Import Excel File</p>
        </header>
        <section class="card-content has-background-white p-5">
          <div class="is-flex is-align-items-center">
            <label class="button has-background-black-bis has-text-white">
              Choose File
              <input type="file" class="is-hidden" @change="handleFileUpload"/>
            </label>
            <span class="ml-3 has-text-black-bis">{{ selectedFile.name || 'No File Chosen' }}</span>
          </div>
        </section>
        <footer class="card-footer has-background-white p-3 is-justify-content-flex-end">
          <button class="button is-outlined has-text-weight-bold mr-2" type="button" @click="closeImportModal">
            Close
          </button>
          <button class="button has-background-black-bis has-text-white has-text-weight-bold"
            type="button" :disabled="!selectedFile.name || isImporting" @click="submitNestedLocFile">
            Submit
          </button>
        </footer>
      </div>
    </o-modal>
  <delete-location
    @deleteCompanyLoc="deleteLocation"
    :isComponentModalActive="isDeleteLocationActive"
    v-if="isDeleteLocationActive"
    :locType="locType"
    @close-modal="isDeleteLocationActive = false; cancelDeleteMode()"
    :locationsList="locationsList"
    :selectedLocs="selectedRows"
  ></delete-location>
  <activity-slider
    :isActive="isActivityActive"
    :id="companyInfo._id"
    module="Company"
    :company="companyInfo._id"
    @update:isActive="isActivityActive = false"
  />
</template>

<script>

import LocationsForm from "@/components/modals/LocationsForm.vue";
import DataTable from "@/components/table-fields/DataTable.vue";
import TableToolbar from "@/components/table-fields/TableToolbar.vue";
import TableGutter from "@/components/TableGutter.vue";
import ExcelImport from '@/components/ExcelImport.vue';
import DeleteLocation from '@/components/modals/DeleteLocation.vue';
import NestedLocation from "../models/NestedLocation";
import companyLocationsCols from "@/components/table-cols/companyLocationsCols.js";
import Companies from '@/models/Companies';
import Locations from "@/models/Locations";
import AddLocation from '@/components/modals/AddLocation.vue';
import useBulkActions from '@/components/utils/BulkActions';
import { onMounted, reactive, toRefs, computed } from "vue";
import { useStore } from 'vuex';
import { ModalProgrammatic } from '@oruga-ui/oruga-next';
import { useToast } from "vue-toastification";
import { DialogProgrammatic } from '@/components/Dialog';
import { get } from 'lodash';
import ActivitySlider from '@/components/ActivitySlider.vue';

export default {
  name: "CompanyLocations",
  components: {
    DataTable,
    TableToolbar,
    TableGutter,
    'locations-form': LocationsForm,
    ExcelImport,
    DeleteLocation,
    ActivitySlider,
  },
  setup() {
    const store = useStore();
    const toast = useToast();
    const data = reactive({
      companyInfo: {},
      showCreateLocationModal: false,
      isLoading: false,
      isEdit: false,
      rowToEdit: {},
      searchText: '',
      tableKey: 0,
      isExcelModalActive: false,
      rowData: {},
      isCheckable: false,
      isDeleteMode: false,
      selectedRows: [],
      refreshToolBar: 0,
      usedlocations: [],
      isDeleteLocationActive: false,
      locType: '',
      locationsList: [],
      skippedLocations: [],
      user: store.state.userData,
      isActivityActive: false,
      selectedFile: {},
      isImporting: false,
    });

    const tableColOpts = computed(() => ({
      ...companyLocationsCols,
      isCheckable: data.isCheckable,
    }));

    const refreshTable = () => {
      data.tableKey += 1;
    };

    const onLocationFormClose = (payload) => {
      if (payload?.added) {
        refreshTable();
      }
      data.showCreateLocationModal = false;
      data.isEdit = false;
      data.rowToEdit = {};
    }

    const loadData = async (commonParam) => {
      const params = {
        type: 'company_location',
        companyId: store.state.userData.company,
        ...commonParam,
        searchText: data.searchText,
        sort: commonParam?.sort[0]?.sortField && commonParam.sort[0].direction === 'asc' ? commonParam.sort[0].sortField : `-${commonParam?.sort[0]?.sortField}`,
      }
      const allLocations = await Locations.allCompanyLocations(params);
      data.locationsList = allLocations;
      return allLocations;
    }

    const setModalActive = (key, val) => {
      data[key] = val
    }

    const onCheckboxToggled = (rows) => {
      data.selectedRows = rows;
    };

    const cancelDeleteMode = () => {
      data.isDeleteMode = false;
      data.isCheckable = false;
      data.selectedRows = [];
      refreshTable();
    };

    const handleFileUpload = (e) => {
      data.selectedFile = e.target.files[0] || {};
    };

    const closeImportModal = () => {
      data.isExcelModalActive = false;
      data.selectedFile = {};
    };

    const submitNestedLocFile = async () => {
      try {
        data.isImporting = true;
        const formData = new FormData();
        formData.append('file', data.selectedFile);
        const { data: res } = await NestedLocation.importNestedLocations(formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (!_.isEmpty(res.addedLocation)) {
          toast.success('Imported Nested Location(s)');
          refreshTable();
        } else {
          toast.error('Please import again with new sub-locations');
        }
      } catch (e) {
        toast.error(get(e, 'data.message') || 'Server error: please contact ManufactOn support');
      } finally {
        data.isImporting = false;
        closeImportModal();
      }
    };

    const handleAction = async (action, payload) => {
      const actionType = action?.event ?? action?.type ?? action;
      const actionMapper = {
        addNew: () => setModalActive('showCreateLocationModal', true),
        openActivity: () => setModalActive('isActivityActive', true),
        edit: (payload) => {
          data.isEdit = true; 
          data.rowToEdit = payload; 
          setModalActive('showCreateLocationModal', true)
        },
        excelImport: () => {
          setModalActive('isExcelModalActive', true);
        },
        deleteLocations: () => {
          data.isCheckable = true;
          data.isDeleteMode = true;
        },
        search: (val) => {
          data.searchText = val;
          refreshTable();
        },
      }
      try {
        await actionMapper[actionType]?.(action?.data ?? payload);
      } catch(e) {
        console.error('Error: ', e)
        toast.error(get(e, 'data.message') || 'Location action error');
      } finally {
        data.isLoading = false
      }
    };

    const openNestedModal = async () => {
      ModalProgrammatic.open({
        component: AddLocation,
        props: {
          for: 'displayNestedLocs',
          hideSave: true,
        },
        trapFocus: true,
        canCancel: false,
        rootClass: 'modal-sm',
      });
    };

    const saveExcelItem = async (locations) => {
      try {
        for (const loc of locations) {
          loc.company = data.user.company._id || data.user.company;
          loc.type = 'company_location';
          await Locations.update(loc, false)
        }
      } catch(e) {
        console.error('Error saving excel item: ', e)
        toast.error(`Error saving excel: ${e.data}`);
      } finally {
        data.isLoading = false;
      }
    };

    const validateExcelItem = async (location) => {
      if (_.isEmpty(location.name) || hasValidLength(location.name, 3)) {
        toast.error('Location name should be atleast 3 characters long!');
        return true;
      } if (_.isEmpty(location.owner) || hasValidLength(location.owner, 3)) {
        toast.error('Owner name should be atleast 3 characters long!');
        return true;
      } if (_.isEmpty(location.street) || hasValidLength(location.street, 3)) {
        toast.error('Street name should be atleast 3 characters long!');
        return true;
      } if (_.isEmpty(location.city) || hasValidLength(location.city, 3)) {
        toast.error('City name should be atleast 3 characters long!');
        return true;
      } if (_.isEmpty(location.state) || hasValidLength(location.state, 2)) {
        toast.error('State name should be atleast 2 characters long!');
        return true;
      } if (!validateZip(location.zip.toString(), true)) {
        return true;
      }
      return false;
    };

    const hasValidLength = (field, min) => {
      return field.length < min;
    };

    const validateZip = (zipCode, showError) => {
			zipCode = !_.isEmpty(zipCode) ? zipCode : '';
			if (!_.inRange(zipCode.length, 1, 25)) {
				if (showError) {
					toast.error('Valid range for Zip length is 1 - 24!')
				}
				return false;
			}
			return true;
		}

    const onCellClicked = async ({ type, data: rowData }) => {
      await handleAction(type, rowData)
    };

    const bulkDeleteLocations = () => {
      if (!data.selectedRows.length) return;
      const isUsedLocation = data.selectedRows.some(loc => data.usedlocations.includes(loc._id));
      if (!isUsedLocation) {
        DialogProgrammatic.confirm({
          title: 'Delete Locations',
          message: `Are you sure you want to delete (${data.selectedRows.length}) selected locations?`,
          okButton: 'Delete',
          cancelButton: 'Cancel',
          type: 'danger',
          onConfirm: async () => {
            data.isLoading = true;
            try {
              await deleteLocation();
              toast.success('Locations deleted successfully');
            } catch (error) {
              toast.error(`Error while deleting locations - ${error.data}`);
            } finally {
              data.isLoading = false;
            }
          },
          rootClass: 'danger-dialog is-header-danger',
        });
      } else {
        data.isDeleteLocationActive = true;
        data.locType = 'company_location';
      }
    };

     const deleteLocation = async (replaceLoc) => {
      try {
        data.isLoading = true;
        const deletedLocations = _.map(data.selectedRows, '_id');
        for (const locationId of deletedLocations) {
          const params = { locations: [locationId] };
          if (!_.isEmpty(replaceLoc)) {
            params.fallback_location = replaceLoc;
          }
          const { locsData } = await Locations.removeLocations(params);
          if (!_.isEmpty(_.get(locsData, 'skippedLocations', []))) {
            data.skippedLocations.push(...locsData.skippedLocations);
          }
        }
        if (data.skippedLocations.length < deletedLocations.length) {
          toast.success('Location(s) deleted');
        }
      } catch (e) {
        console.error('Error archiving location: ', e)
        toast.error(`Error archiving - ${e.data}.  Please contact ManufactOn support`);
      } finally {
        data.isLoading = false;
        data.isDeleteLocationActive = false;
        data.isCheckable = false;
        data.isDeleteMode = false;
        data.selectedRows = [];
        data.isLoading = false;
        data.refreshToolBar += 1;
        refreshTable();
      }
    }

    onMounted(async () => {
      try {
        data.isLoading = true;
        data.companyInfo = await Companies.getOne(store.state.userData.company);
        data.usedlocations = await Companies.usedLocations();
      } catch (error) {
        console.error('Error fetching company info: ', error);
      } finally {
        data.isLoading = false;
      }
    });
    return {
      ...toRefs(data),
      tableColOpts,
      loadData,
      handleAction,
      setModalActive,
      openNestedModal,
      onCellClicked,
      onLocationFormClose,
      refreshTable,
      saveExcelItem,
      hasValidLength,
      validateZip,
      onCheckboxToggled,
      bulkDeleteLocations,
      cancelDeleteMode,
      deleteLocation,
      validateExcelItem,
      handleFileUpload,
      submitNestedLocFile,
      closeImportModal,

    };
  },
};
</script>